import {
    CountryPathAndReportProps,
    CountryReportInfo,
    ReportDataProps,
    TierGraphicProp
} from "../interfaces/RankingsAndLevelsInterfaces";
// @ts-ignore
import * as d3 from 'd3';
// @ts-ignore
import * as topojson from 'topojson-client';
// @ts-ignore
import * as countries from 'iso-3166-1-codes';

import worldData from '../data/world.json'
import countryData from '../data/world3.json'
import Colors from "../assets/colors/Colors";


const RankingsAndLevelsUtils = (() => {
    enum lifeValues {
        life,
        noLife
    }

    const handleQueryParams = (location: any, history: any, isLife: boolean, setIsLife: (bool: boolean) => void, baseUrl: string) => {
        const querySearch = location.search.match(/life=[^&]*&?/);
        if(querySearch && querySearch[0]){
            let lifeValue = (querySearch[0] === `life=${lifeValues.life}`) ? lifeValues.life : lifeValues.noLife;

            if(!isLife && lifeValue === lifeValues.life){
                history.push(`${baseUrl}?life=${lifeValues.life}`)
                setIsLife(true);
            }
            if(isLife && lifeValue === lifeValues.noLife){
                history.push(`${baseUrl}?life=${lifeValues.noLife}`)
                setIsLife(false);
            }
        }
    };

    const filterLifeRecords = (reportData: ReportDataProps, isLife: boolean) => {
        let reportDataClone: ReportDataProps = JSON.parse(JSON.stringify(reportData));
        reportDataClone.records = reportDataClone.records.filter((record) => record.is_life === isLife);
        return reportDataClone;
    };

    const generateWorld = (reportData: ReportDataProps, isLife: boolean): CountryPathAndReportProps[] => {
        const projection      = d3.geoMercator().scale(200).translate([1300 / 2, 713 / 2]);
        const svgWorldMap     = topojson.feature(worldData, worldData.objects.countries).features;
        const path            = d3.geoPath().projection(projection);
        const cloneReportData = filterLifeRecords(reportData, isLife);

        const svgPaths = svgWorldMap.map((a: any) => {
            const countryReportInfo = cloneReportData.records.find((data) => {
                return data.country_code === countries.byNumeric().get(a.id)?.alpha3
            });

            const countryInfo = reportData.country_records.find((data) => {
                return data.country_code === countries.byNumeric().get(a.id)?.alpha3
            });

            return { countryInfo: {...countries.byNumeric().get(a.id), ...countryReportInfo, ...countryInfo }, path: path(a) }
        });

        return svgPaths;
    };

    const getCountryPath = (countryCode: string, reportData: ReportDataProps | null, isLife: boolean): CountryPathAndReportProps | undefined => {
        if(!reportData) return;

        const countryInfo       = countries.byAlpha3().get(countryCode);
        if(!countryInfo) return;

        const svgWorldMap       = topojson.feature(countryData, countryData.objects.countries).features;
        const cloneReportData   = filterLifeRecords(reportData, isLife);
        const countryReportData = cloneReportData.records.find((data) => (data.country_code === countryCode));
        const countryRecordData = cloneReportData.country_records.find((data) => (data.country_code === countryCode));

        const svgCountry = svgWorldMap.find((a: any) => {
            return (countryInfo.numeric === a.id)
        });

        const width = 100;
        const height = 100;

        let projection  = d3.geoMercator().scale(1).translate([0, 0]);
        let path        = d3.geoPath().projection(projection);
        let b           = path.bounds(svgCountry);
        let s           = .95 / Math.max((b[1][0] - b[0][0]) / width, (b[1][1] - b[0][1]) / height);
        let t           = [(width - s * (b[1][0] + b[0][0])) / 2, (height - s * (b[1][1] + b[0][1])) / 2];

        // @ts-ignore
        const projection1 = projection.scale(s).translate(t);
        const path2       = d3.geoPath().projection(projection1);

        return { countryReportData: {...countryInfo, ...countryReportData, ...countryRecordData }, path: path2(svgCountry) }
    };

    const handleTierColor = (country: any, isLife: boolean) => {
        if (!country.countryInfo.tier) return isLife ? Colors.tiers.defaultGray : Colors.tiers.defaultWhite;
        switch (country.countryInfo.tier) {
            case (1):
                return Colors.tiers.tier1;
            case (2):
                return Colors.tiers.tier2;
            case (3):
                return Colors.tiers.tier3;
            default:
                return isLife ? Colors.tiers.defaultGray : Colors.tiers.defaultWhite;
        }
    };

    const getCountryInfo = (countryCode: string, last10YearReports: ReportDataProps[] | null,
                            lastAvailableReport: ReportDataProps | null, isLife: boolean): CountryReportInfo | undefined => {
        if(!last10YearReports || !lastAvailableReport) return;

        const countryInfo       = countries.byAlpha3().get(countryCode);
        if(!countryInfo) return;

        const cloneReportData   = filterLifeRecords(lastAvailableReport, isLife);
        const countryReportData = cloneReportData.records.find((data) => (data.country_code === countryCode));
        const countryRecordInfo = lastAvailableReport.country_records.find((data) => (data.country_code === countryCode));

        let countryAllReportData: any = {};
        Object.keys(last10YearReports).forEach((year) => {
            const recordData  = last10YearReports[Number(year)].records.find((data) => (data.country_code === countryCode));
            const countryData = last10YearReports[Number(year)].country_records.find((data) => (data.country_code === countryCode));

            countryAllReportData[Number(year)] = {...recordData, ...countryData};
        });

        return { countryLastReportData: {...countryInfo, ...countryReportData, ...countryRecordInfo }, countryAllReportData: countryAllReportData }
    };

    const getReportGips = (reportsAvailables: ReportDataProps, tierCode: string, isLife: boolean): TierGraphicProp[] | undefined => {
        if(!reportsAvailables) return;
        let cloneReportData   = filterLifeRecords(reportsAvailables, isLife);

        const tier = tierCode.replace('tier','');
        cloneReportData.records = cloneReportData.records.filter((record) => record.tier === Number(tier));

        return cloneReportData.records.map((country) => {
            let info = countries.byAlpha3().get(country.country_code);
            return { name: info.name, gip: Number(country.gip), alpha2: info.alpha2, alpha3: info.alpha3 }
        })
    };

    return {
        lifeValues: lifeValues,
        handleQueryParams: (location: any, history: any, isLife: boolean,
                            setIsLife: (bool: boolean) => void, baseUrl: string) => (
            handleQueryParams(location, history, isLife, setIsLife, baseUrl)
        ),
        handleTierColor: (country: any, isLife: boolean) => handleTierColor(country, isLife),
        generateWorld: (tierData: ReportDataProps, isLife: boolean): CountryPathAndReportProps[] => (
            generateWorld(tierData, isLife)
        ),
        getCountryPath: (countryCode: string, tierData: ReportDataProps | null, isLife: boolean): CountryPathAndReportProps | undefined =>(
            getCountryPath(countryCode, tierData, isLife)
        ),
        getCountryInfo: (countryCode: string, last10YearReports: ReportDataProps[] | null,
                         lastAvailableReport: ReportDataProps | null, isLife: boolean): CountryReportInfo | undefined => (
            getCountryInfo(countryCode, last10YearReports, lastAvailableReport, isLife)
        ),
        getReportGips: (reportsAvailables: ReportDataProps, tierCode: string, isLife: boolean): TierGraphicProp[] | undefined  => (
            getReportGips(reportsAvailables, tierCode, isLife)
        )
    }
})();

export default RankingsAndLevelsUtils;
